import { useEffect, useRef, useState } from 'react';

import { Row, TableViewHandle } from '~components/table-view';

export interface EditableRow extends Row {
  persisted?: boolean;
  backendId?: string | number;
  client_id?: number | null;
}

export type ConfirmOutcome = 'saved' | 'clean' | 'invalid' | 'failed';

interface UseEditableTableRowsOptions<T extends EditableRow> {
  /** Persisted rows from the store — the snapshot all edits are diffed against at confirm time. */
  source: T[];
  /** Seeds consumer-defined defaults onto rows added via the table's Add Row control. */
  getDefaultRow: () => T;
  onCreate: (row: T) => Promise<void>;
  onUpdate?: (row: T) => Promise<void>;
  onDelete?: (row: T) => Promise<void>;
  /**
   * Optional fields to compare for change detection. When omitted, every key on the row
   * (excluding meta flags `id`, `persisted`, `backendId`, `_meta`) is compared.
   */
  diffKeys?: (keyof T)[];
}

const META_KEYS = new Set(['id', 'persisted', 'backendId', '_meta']);

function rowFingerprint<T extends EditableRow>(row: T, keys?: (keyof T)[]): string {
  if (keys) {
    return JSON.stringify(keys.map((k) => row[k] ?? null));
  }
  const entries = Object.entries(row)
    .filter(([k]) => !META_KEYS.has(k))
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}

/**
 * State + batch-confirm logic for modals that edit table rows with table-view directly.
 *
 * The table is rendered in `editonly` mode by the consumer; this hook owns the row state,
 * seeds Add Row defaults, computes the created/updated/deleted diff against the loaded
 * snapshot, and runs the persistence calls when the user confirms.
 */
function useEditableTableRows<T extends EditableRow>({
  source,
  getDefaultRow,
  onCreate,
  onUpdate,
  onDelete,
  diffKeys,
}: UseEditableTableRowsOptions<T>) {
  const [rows, setRows] = useState<T[]>(source);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const tableRef = useRef<TableViewHandle>(null);

  // Snapshot of the data as loaded — used to compute the diff at confirm time. Held as state
  // (not derived) so a partially-failed confirm can absorb the operations that DID persist,
  // preventing a re-confirm from replaying them (e.g. re-deleting an already-deleted row).
  const [snapshot, setSnapshot] = useState<T[]>(() => source.map((row) => ({ ...row })));

  useEffect(() => {
    setRows(source);
    setSnapshot(source.map((row) => ({ ...row })));
    setError(null);
  }, [source]);

  // v3 fires `onChange` for cell edits, Add Row clicks, and delete clicks. Newly-added rows
  // arrive as bare `{ id }` — seed them with `getDefaultRow()` so consumer-defined fields make
  // it through, and leave `persisted` unset so the diff routes them to onCreate.
  const handleChange = (next: Row[]) => {
    setRows((prev) => {
      const prevIds = new Set(prev.map((row) => row.id));
      return next.map((row) => {
        if (prevIds.has(row.id)) return row as T;
        return { ...getDefaultRow(), id: row.id } as T;
      });
    });
  };

  // Only new or changed rows must pass column validation. Untouched persisted rows are
  // grandfathered — legacy data (e.g. null end dates) predating the Yup schemas would
  // otherwise block the entire confirm even when the user never touched the row.
  const canRowValidate = (row: Row) => {
    const original = snapshot.find((snapshotRow) => snapshotRow.id === row.id);
    if (!original) return true;
    return rowFingerprint(original as T, diffKeys) !== rowFingerprint(row as T, diffKeys);
  };

  const computeDiff = () => {
    const snapshotById = new Map(snapshot.map((row) => [row.id, row as T]));
    const currentIds = new Set(rows.map((row) => row.id));

    const created: T[] = [];
    const updated: T[] = [];
    const deleted: T[] = [];

    for (const row of rows) {
      const original = snapshotById.get(row.id);
      if (!original) {
        created.push(row);
      } else if (rowFingerprint(original, diffKeys) !== rowFingerprint(row, diffKeys)) {
        updated.push(row);
      }
    }

    for (const original of snapshot) {
      if (!currentIds.has(original.id)) {
        deleted.push(original as T);
      }
    }

    return { created, updated, deleted };
  };

  const confirmChanges = async (): Promise<ConfirmOutcome> => {
    setError(null);

    const isValid = (await tableRef.current?.validate()) ?? true;
    if (!isValid) {
      setError('Please correct the highlighted fields before confirming.');
      return 'invalid';
    }

    const { created, updated, deleted } = computeDiff();
    if (created.length === 0 && updated.length === 0 && deleted.length === 0) {
      return 'clean';
    }

    setIsProcessing(true);
    const applied = { deleted: [] as T[], updated: [] as T[], created: [] as T[] };
    try {
      // Run deletes first so any unique-constraint violations clear before re-inserts.
      for (const row of deleted) {
        if (onDelete) await onDelete(row);
        applied.deleted.push(row);
      }
      for (const row of updated) {
        if (onUpdate) await onUpdate(row);
        applied.updated.push(row);
      }
      for (const row of created) {
        await onCreate(row);
        applied.created.push(row);
      }
      return 'saved';
    } catch (err) {
      // Absorb the operations that persisted before the failure into the snapshot so a
      // re-confirm only retries what is still outstanding.
      setSnapshot((prev) => {
        const deletedIds = new Set(applied.deleted.map((row) => row.id));
        const updatedById = new Map(applied.updated.map((row) => [row.id, row]));
        const next = prev
          .filter((row) => !deletedIds.has(row.id))
          .map((row) => ({ ...(updatedById.get(row.id) ?? row) }));
        return [...next, ...applied.created.map((row) => ({ ...row }))];
      });
      setError((err as Error).message || 'Failed to save changes');
      return 'failed';
    } finally {
      setIsProcessing(false);
    }
  };

  return { rows, tableRef, error, isProcessing, handleChange, canRowValidate, confirmChanges };
}

export default useEditableTableRows;
